import { useState } from 'react';
import { Pencil, Tag, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import {
  Apk,
  apkLabelClass,
  setApkLabel,
  softDeleteApk,
  useApks,
} from '@/lib/api/apks';
import { UploadApkDialog } from './upload-apk-dialog';

/** Inline label editor: badge + pencil → input (Enter saves, Esc cancels).
 *  Labels are moving pointers — saving one clears it off any other APK. */
function LabelCell({ apk, apks }: { apk: Apk; apks: Apk[] | undefined }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const movedFrom = await setApkLabel(apk.id, value, apks);
      const label = value.trim().toUpperCase();
      toast.success(
        label
          ? `${apk.name} labeled ${label}` +
              (movedFrom ? ` (moved from ${movedFrom})` : '')
          : `Label removed from ${apk.name}`
      );
      setEditing(false);
    } catch (error) {
      console.error('set apk label failed', error);
      toast.error('Failed to set label');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <Input
        autoFocus
        value={value}
        disabled={saving}
        placeholder="NV-LATEST"
        className="h-8 w-[160px] uppercase"
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            save();
          }
          if (e.key === 'Escape') setEditing(false);
        }}
        onBlur={() => !saving && setEditing(false)}
      />
    );
  }
  return (
    <div className="flex items-center gap-1">
      {apk.label ? (
        <Badge
          variant="outline"
          className={`gap-1 ${apkLabelClass(apk.label)}`}
        >
          <Tag className="h-3 w-3" />
          {apk.label}
        </Badge>
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6"
        aria-label={`Edit label of ${apk.name}`}
        onClick={() => {
          setValue(apk.label ?? '');
          setEditing(true);
        }}
      >
        <Pencil className="h-3 w-3" />
      </Button>
    </div>
  );
}

function formatSize(bytes: number) {
  if (!bytes) return '--';
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function Apks() {
  const [apks] = useApks();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [apkToDelete, setApkToDelete] = useState<Apk | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!apkToDelete) return;
    setIsDeleting(true);
    try {
      await softDeleteApk(apkToDelete.id);
      toast.success('APK deleted');
      setApkToDelete(null);
    } catch (error) {
      console.error('delete apk failed', error);
      toast.error('Failed to delete APK');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="w-full pb-4">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">APKs</h1>
        <Button type="button" onClick={() => setUploadOpen(true)}>
          Upload APK
        </Button>
      </div>

      <Separator className="my-6" />

      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Label</TableHead>
              <TableHead>Box type</TableHead>
              <TableHead>File</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead className="w-px" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {apks?.length ? (
              apks.map((apk) => (
                <TableRow key={apk.id}>
                  <TableCell className="font-medium">{apk.name}</TableCell>
                  <TableCell>
                    <LabelCell apk={apk} apks={apks} />
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={apk.brand === 'NVIDIA' ? 'default' : 'secondary'}
                    >
                      {apk.brand}
                    </Badge>
                  </TableCell>
                  <TableCell className="break-all text-muted-foreground">
                    {apk.fileName}
                  </TableCell>
                  <TableCell>{formatSize(apk.size)}</TableCell>
                  <TableCell>
                    <div>{apk.uploadDate?.toLocaleDateString() ?? '--'}</div>
                    <div className="text-xs text-muted-foreground">
                      {apk.uploadedBy}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Delete ${apk.name}`}
                        onClick={() => setApkToDelete(apk)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  No APKs uploaded yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <UploadApkDialog open={uploadOpen} onOpenChange={setUploadOpen} />

      <Dialog
        open={!!apkToDelete}
        onOpenChange={(open) => {
          if (!open) setApkToDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete APK</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{apkToDelete?.name}"? It will no
              longer be available to install.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setApkToDelete(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
